import React from 'react';
import { TargetType } from '../../types/notice.types';
import { SupplierOption, RiderOption } from '../../hooks/useNoticeForm';

interface NoticeTargetSelectorProps {
    targetType: TargetType;
    targetIds: string[];
    supplierOptions: SupplierOption[];
    optionsLoading: boolean;
    searchKeyword: string;
    riderFilterSuppId: string;
    currentOptionsLabel: string;
    selectedItems: (SupplierOption | RiderOption)[];
    currentFilteredOptions: (SupplierOption | RiderOption)[];
    currentTotalOptions: (SupplierOption | RiderOption)[];
    onSearchChange: (keyword: string) => void;
    onRiderFilterChange: (suppId: string) => void;
    onAddTarget: (value: string) => void;
    onRemoveTarget: (value: string) => void;
    onSelectAll: () => void;
    onClearAll: () => void;
}

export const NoticeTargetSelector: React.FC<NoticeTargetSelectorProps> = ({
                                                                              targetType,
                                                                              targetIds,
                                                                              supplierOptions,
                                                                              optionsLoading,
                                                                              searchKeyword,
                                                                              riderFilterSuppId,
                                                                              currentOptionsLabel,
                                                                              selectedItems,
                                                                              currentFilteredOptions,
                                                                              currentTotalOptions,
                                                                              onSearchChange,
                                                                              onRiderFilterChange,
                                                                              onAddTarget,
                                                                              onRemoveTarget,
                                                                              onSelectAll,
                                                                              onClearAll,
                                                                          }) => {
    if (optionsLoading) {
        return <div className="loading-text">목록을 불러오는 중...</div>;
    }

    if (currentTotalOptions.length === 0) {
        return <div className="empty-text">선택 가능한 {currentOptionsLabel}가 없습니다.</div>;
    }

    return (
        <div className="target-selector">
            {selectedItems.length > 0 && (
                <div className="selected-tags">
                    {selectedItems.map((item) => (
                        <span key={item.value} className="tag">
                            {item.label}
                            <button
                                type="button"
                                className="tag-remove"
                                onClick={() => onRemoveTarget(item.value)}
                            >
                                &times;
                            </button>
                        </span>
                    ))}
                </div>
            )}

            {targetType === 'R' && (
                <div className="rider-filter-wrapper" style={{ marginBottom: '8px' }}>
                    <label style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px', display: 'block' }}>
                        협력사로 필터링
                    </label>
                    <select
                        value={riderFilterSuppId}
                        onChange={(e) => onRiderFilterChange(e.target.value)}
                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                    >
                        <option value="">전체 협력사</option>
                        {supplierOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label} ({opt.status})
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <div className="search-input-wrapper">
                <input
                    type="text"
                    value={searchKeyword}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder={
                        targetType === 'R'
                            ? '라이더 검색 (ID, 이름)'
                            : '협력사 검색 (운영 중 상태만 검색 가능)'
                    }
                    className="search-input"
                />
                {searchKeyword && (
                    <button type="button" className="search-clear" onClick={() => onSearchChange('')}>
                        &times;
                    </button>
                )}
            </div>

            {(targetType === 'S' || targetType === 'SR') && (
                <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px', marginBottom: '8px' }}>
                    * 운영 중 상태의 협력사만 검색이 가능합니다. 계약 중 협력사는 목록에서 직접 선택해주세요.
                </div>
            )}

            <div className="select-actions">
                <button
                    type="button"
                    className="btn btn-sm btn-outline"
                    onClick={onSelectAll}
                    disabled={currentFilteredOptions.length === 0}
                >
                    {searchKeyword || riderFilterSuppId ? '검색결과 전체 선택' : '전체 선택'}
                </button>
                <button
                    type="button"
                    className="btn btn-sm btn-outline"
                    onClick={onClearAll}
                    disabled={targetIds.length === 0}
                >
                    전체 해제
                </button>
                <span className="select-count">
                    선택: {targetIds.length}개 / 전체: {currentTotalOptions.length}개
                </span>
            </div>

            <div className="options-list">
                {currentFilteredOptions.length === 0 ? (
                    <div className="empty-text">
                        {searchKeyword || riderFilterSuppId
                            ? '검색 결과가 없습니다.'
                            : '모든 항목이 선택되었습니다.'}
                    </div>
                ) : (
                    currentFilteredOptions.map((opt) => {
                        const isActiveStatus =
                            targetType === 'R'
                                ? (opt as RiderOption).isActive
                                : (opt as SupplierOption).isOperating;
                        return (
                            <div
                                key={opt.value}
                                className={`option-item ${!isActiveStatus ? 'inactive' : ''}`}
                                onClick={() => onAddTarget(opt.value)}
                            >
                                <span className="option-label">
                                    {opt.label}
                                    {'status' in opt && opt.status && (
                                        <span style={{
                                            marginLeft: '8px',
                                            fontSize: '11px',
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                            background: isActiveStatus ? '#dcfce7' : '#fef9c3',
                                            color: isActiveStatus ? '#166534' : '#854d0e',
                                        }}>
                                            {opt.status}
                                        </span>
                                    )}
                                </span>
                                <span className="option-add">+ 추가</span>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};
